import { lazy } from "react";
import { Navigate, RouteObject, useLocation } from "react-router-dom";

import AppErrorBoundary from "router/AppErrorBoundary/AppErrorBoundary";
import AccountLayout from "router/layouts/AccountLayout";
import AppShellLayout from "router/layouts/AppShellLayout";
import { createConditionalLayout } from "router/layouts/ConditionalLayout";
import IdentifiedAuthLayout from "router/layouts/IdentifiedAuthLayout";
import InsuranceLayout from "router/layouts/InsuranceLayout";
import JourneyLayout from "router/layouts/JourneyLayout";
import RootLayout from "router/layouts/RootLayout";
import VerifiedAuthLayout from "router/layouts/VerifiedAuthLayout";
import { Paths } from "router/routes/paths";

import { startCase } from "lodash-es";

/* ─── Lazy-loaded route components ────────────────────────────────────────── */

const AboutADRoute = lazy(() => import("router/AboutADRoute/AboutADRoute"));
const AccountDocumentById = lazy(
  () => import("containers/Account/AccountDocuments/AccountDocumentById")
);
const AccountDocumentRoutes = lazy(
  () => import("router/AccountDocumentRoutes/AccountDocumentRoutes")
);
const AccountEditRoutes = lazy(() => import("router/AccountEditRoutes/AccountEditRoutes"));
const AccountPolicyReinstatement = lazy(
  () => import("containers/Account/AccountPolicyReinstatement/AccountPolicyReinstatement")
);
const AccountProfileRoutes = lazy(() => import("router/AccountProfileRoutes/AccountProfileRoutes"));
const AccountSecurity = lazy(() => import("containers/Account/AccountSecurity/AccountSecurity"));
const Account = lazy(() => import("containers/Account/Account"));
const AffiliateEstimate = lazy(() => import("containers/AffiliateEstimate/AffiliateEstimate"));
const BeneficiaryRoute = lazy(() => import("router/BeneficiaryRoute/BeneficiaryRoute"));
const CoverageDeniedRoute = lazy(() => import("router/CoverageDeniedRoute/CoverageDeniedRoute"));
const CoverageEditorRoute = lazy(() => import("router/CoverageEditorRoute/CoverageEditorRoute"));
const CoveragePreviewRoute = lazy(() => import("router/CoverageEditorRoute/CoveragePreviewRoute"));
const CreateNewPasswordRoute = lazy(
  () => import("router/CreateNewPasswordRoute/CreateNewPasswordRoute")
);
const CreatePhoneRoute = lazy(() => import("router/CreatePhoneRoute/CreatePhoneRoute"));
const CustomerSupport = lazy(() => import("containers/CustomerSupport/CustomerSupport"));
const DashboardRoute = lazy(() => import("router/DashboardRoute/DashboardRoute"));
const DevConfigurationRoute = lazy(
  () => import("router/DevConfigurationRoute/DevConfigurationRoute")
);
const Disclosure = lazy(() => import("containers/Disclosure/Disclosure"));
const FlowRoute = lazy(() => import("router/FlowRoute/FlowRoute"));
const FulfillmentRoute = lazy(() => import("router/FulfillmentRoute/FulfillmentRoute"));
const IneligibleWarningRoute = lazy(
  () => import("router/IneligibleWarningRoute/IneligibleWarningRoute")
);
const InsuranceBillingRoutes = lazy(
  () => import("router/InsuranceBillingRoutes/InsuranceBillingRoutes")
);
const InsuranceHandoffRoute = lazy(
  () => import("router/InsuranceHandoffRoute/InsuranceHandoffRoute")
);
const InsuranceInReview = lazy(() => import("containers/InsuranceInReview/InsuranceInReview"));
const InsuranceMarketingRoute = lazy(
  () => import("router/InsuranceMarketingRoute/InsuranceMarketingRoute")
);
const InsuranceRecapture = lazy(() => import("containers/InsuranceRecapture/InsuranceRecapture"));
const InsuranceTabRoutes = lazy(() => import("router/InsuranceTabRoutes/InsuranceTabRoutes"));
const LoginRoute = lazy(() => import("router/LoginRoute/LoginRoute"));
const ManualReviewRoute = lazy(
  () => import("router/InsuranceRoutes/ManualReviewRoute/ManualReviewRoute")
);
const NotFoundRoute = lazy(() => import("router/NotFoundRoute/NotFoundRoute"));
const NotFound = lazy(() => import("containers/NotFound/NotFound"));
const PendingDetailsRoute = lazy(
  () => import("router/PolicyRoute/PendingDetailsRoute.tsx/PendingDetailsRoute")
);
const PolicyConfirmUpdateRoute = lazy(
  () => import("router/PolicyRoute/PolicyConfirmUpdateRoute/PolicyConfirmUpdateRoute")
);
const PolicyReinstatementDetails = lazy(
  () => import("containers/PolicyReinstatement/PolicyReinstatementDetails")
);
const ProductSelectRoute = lazy(() => import("router/ProductSelectRoute/ProductSelectRoute"));
const QuoteRoute = lazy(() => import("router/QuoteRoute/QuoteRoute"));
const ResetPasswordRoute = lazy(() => import("router/ResetPasswordRoute/ResetPasswordRoute"));
const VerifyEmailRoute = lazy(() => import("router/VerifyEmailRoute/VerifyEmailRoute"));
const WelcomeRoute = lazy(() => import("router/WelcomeRoute/WelcomeRoute"));
const WyshBuilderLiteRoute = lazy(() => import("router/WyshBuilderLiteRoute/WyshBuilderLiteRoute"));
const WyshesRoute = lazy(() => import("router/WyshesRoute/WyshesRoute"));

/* ─── Condition layout instances ──────────────────────────────────────────── */

const HasEstimateLayout = createConditionalLayout(["user.hasEstimate"]);
const HasInsuranceLayout = createConditionalLayout(["memberships.hasInsurance"]);

/* ─── Legacy redirect helpers ──────────────────────────────────────────────── */

const CoverageRedirect = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace(/^\/coverage/, "/products/insurance")} replace />;
};

const SettingsBillingRedirect = () => {
  const { pathname } = useLocation();
  return (
    <Navigate
      to={pathname.replace(/^\/settings\/billing/, "/products/insurance/billing")}
      replace
    />
  );
};

const SettingsRedirect = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace(/^\/settings/, "/account")} replace />;
};

/* ─── Pre-auth routes (bypass AppShell) ─────────────────────────────────── */

const preAuthRoutes: RouteObject[] = [
  { path: "config/*", Component: DevConfigurationRoute },
  { path: "verify-email/:token", Component: VerifyEmailRoute, handle: { testId: "verify-email" } },
];

/* ─── Public routes (no auth required) ─────────────────────────────────── */

const publicRoutes: RouteObject[] = [
  { path: "login", Component: LoginRoute, handle: { title: "Login", testId: "login" } },
  {
    path: "forgot-password",
    Component: ResetPasswordRoute,
    handle: { title: "Forgot Password", testId: "forgot-password" },
  },
  {
    path: "create-new-password/:token",
    Component: CreateNewPasswordRoute,
    handle: { title: "Create New Password", testId: "create-new-password" },
  },
  {
    path: "disclosure/:slug",
    Component: Disclosure,
    handle: {
      title: (params: Record<string, string | undefined>) => startCase(params.slug),
      testId: "disclosure",
    },
  },
  { path: "signup", element: <Navigate to={Paths.products.insurance.welcome} replace /> },
  {
    path: "ineligible/:reason?",
    Component: IneligibleWarningRoute,
    handle: { title: "Ineligible Coverage", testId: "ineligible" },
  },
];

/* ─── Legacy redirect routes ────────────────────────────────────────────── */

const legacyRedirectRoutes: RouteObject[] = [
  { path: "coverage/*", Component: CoverageRedirect },
  { path: "settings/billing/*", Component: SettingsBillingRedirect },
  { path: "settings/*", Component: SettingsRedirect },
];

/* ─── Insurance sub-routes (/products/insurance/*) ─────────────────────── */

const insuranceRoutes: RouteObject[] = [
  // Public insurance routes
  { path: "welcome", Component: WelcomeRoute, handle: { title: "Get Started", testId: "welcome" } },
  {
    path: "get-wysh-estimate",
    Component: AffiliateEstimate,
    handle: { title: "Getting Your Estimate", testId: "get-wysh-estimate" },
  },

  // Identified auth gate
  {
    Component: IdentifiedAuthLayout,
    children: [
      {
        path: "get-started",
        Component: InsuranceMarketingRoute,
        handle: { testId: "get-started" },
      },
      {
        Component: HasInsuranceLayout,
        children: [
          {
            path: "wysh-builder/*",
            Component: WyshBuilderLiteRoute,
            handle: { testId: "wysh-builder" },
          },
        ],
      },
      {
        Component: HasEstimateLayout,
        children: [
          { path: "estimate", Component: InsuranceRecapture, handle: { testId: "estimate" } },
        ],
      },
      { index: true, Component: InsuranceTabRoutes, handle: { testId: "insurance" } },
      { path: "*", Component: InsuranceTabRoutes },
    ],
  },

  // Verified auth gate
  {
    Component: VerifiedAuthLayout,
    children: [
      { path: "billing/*", Component: InsuranceBillingRoutes, handle: { testId: "billing" } },
      { path: "manual-review", Component: ManualReviewRoute, handle: { testId: "manual-review" } },
      { path: "in-review", Component: InsuranceInReview, handle: { testId: "in-review" } },
      {
        Component: HasInsuranceLayout,
        children: [
          {
            path: "confirm-update",
            Component: PolicyConfirmUpdateRoute,
            handle: { title: "Confirm Policy Changes", testId: "confirm-update" },
          },
          {
            path: ":policyId/pending-details",
            Component: PendingDetailsRoute,
            handle: { title: "Pending Policy Details", testId: "pending-details" },
          },
        ],
      },
    ],
  },
];

/* ─── Products routes (/products/*) ────────────────────────────────────── */

const productsRoutes: RouteObject[] = [
  { path: "select", Component: ProductSelectRoute },
  { path: "insurance", Component: InsuranceLayout, children: insuranceRoutes },
];

/* ─── Account sub-routes (/account/*) ──────────────────────────────────── */

const accountSubRoutes: RouteObject[] = [
  { path: "change-password", Component: AccountSecurity, handle: { testId: "change-password" } },
  { path: "document/:id", Component: AccountDocumentById, handle: { testId: "account-document" } },
  {
    path: "documents/*",
    Component: AccountDocumentRoutes,
    handle: { testId: "account-documents" },
  },
  { path: "profile/edit/*", Component: AccountEditRoutes, handle: { testId: "profile-edit" } },
  { path: "profile/*", Component: AccountProfileRoutes, handle: { testId: "profile" } },
  {
    path: "policy-reinstatement",
    Component: AccountPolicyReinstatement,
    handle: { testId: "policy-reinstatement" },
  },
  {
    path: "policy-reinstatement/:policyId",
    Component: PolicyReinstatementDetails,
    handle: { testId: "policy-reinstatement-details" },
  },
  { index: true, Component: Account },
  { path: "*", Component: NotFound },
];

/* ─── Identified auth routes ────────────────────────────────────────────── */

const identifiedRoutes: RouteObject[] = [
  { index: true, Component: DashboardRoute, handle: { title: "Dashboard", testId: "dashboard" } },
  {
    path: "flow/:slug",
    Component: FlowRoute,
    handle: {
      title: (params: Record<string, string | undefined>) => startCase(params.slug),
      testId: "flow",
    },
  },
  {
    path: "fulfillment/:action?/:policyId?",
    Component: FulfillmentRoute,
    handle: { title: "Fulfillment", testId: "fulfillment" },
  },
  { path: "create-phone", Component: CreatePhoneRoute, handle: { title: "Create Phone" } },
  { path: "wysh-builder/*", Component: WyshBuilderLiteRoute, handle: { testId: "wysh-builder" } },
  {
    path: "getting-coverage",
    Component: InsuranceHandoffRoute,
    handle: { testId: "getting-coverage" },
  },

  // Condition-gated insurance routes at identified auth level
  {
    Component: HasInsuranceLayout,
    children: [
      {
        path: "coverage-denied/:type?",
        Component: CoverageDeniedRoute,
        handle: { title: "Coverage Denied", testId: "coverage-denied" },
      },
      {
        path: "coverage-preview",
        Component: CoveragePreviewRoute,
        handle: { title: "Coverage Preview", testId: "coverage-preview" },
      },
      {
        path: "edit-coverage/:type/:billingFrequency?",
        Component: CoverageEditorRoute,
        handle: { title: "Edit Coverage", testId: "edit-coverage" },
      },
    ],
  },
];

/* ─── Verified auth routes ──────────────────────────────────────────────── */

const verifiedRoutes: RouteObject[] = [
  {
    path: "beneficiary/:lineOfBusiness",
    Component: BeneficiaryRoute,
    handle: { title: "Beneficiary Overview", testId: "beneficiary" },
  },
  {
    path: "account",
    Component: AccountLayout,
    handle: { title: "Account", testId: "account" },
    children: accountSubRoutes,
  },
  { path: "wyshes/*", Component: WyshesRoute, handle: { testId: "wyshes" } },
  {
    Component: HasInsuranceLayout,
    children: [
      { path: "about-ad/*", Component: AboutADRoute, handle: { testId: "about-ad" } },
      {
        path: "review-quote",
        Component: QuoteRoute,
        handle: { title: "Quote Review", testId: "review-quote" },
      },
    ],
  },
  { path: "customer-support", Component: CustomerSupport, handle: { testId: "customer-support" } },
];

/* ─── Root config ───────────────────────────────────────────────────────── */

export const appRoutes: RouteObject[] = [
  {
    Component: RootLayout,
    ErrorBoundary: AppErrorBoundary,
    children: [
      ...preAuthRoutes,
      {
        Component: AppShellLayout,
        children: [
          ...publicRoutes,
          ...legacyRedirectRoutes,
          { path: "products", children: productsRoutes },
          {
            Component: JourneyLayout,
            children: [
              { Component: IdentifiedAuthLayout, children: identifiedRoutes },
              { Component: VerifiedAuthLayout, children: verifiedRoutes },
            ],
          },
          { path: "*", Component: NotFoundRoute },
        ],
      },
    ],
  },
];
